import { appearanceRuntime } from "@/services/appearanceRuntime";
import { normalizeConversationPresentation } from "@/services/messageConversationPresentation";
import { createSiblingProposalDraft } from "@/services/siblingProposalDraft";

const normalizePrivateMessagePresentation = (value) => {
  try {
    return normalizeConversationPresentation(value);
  } catch (_error) {
    return null;
  }
};

const createEconomyRestrictionsState = ({
  ownerUID = null,
  orgUID = null,
  requestGeneration = 0,
  orgAllowTrades = false,
  walletAllowTrades = false,
  loaded = false,
  error = null,
} = {}) => ({
  ownerUID,
  orgUID,
  requestGeneration,
  orgAllowTrades,
  walletAllowTrades,
  loaded,
  error,
});

const ownsActiveEconomyContext = (economyRestrictions, rootGetters) => {
  if (!rootGetters) return true;
  const activeUserUID =
    rootGetters.userCredentials?.data?.uid ||
    rootGetters.user?.data?.uid ||
    rootGetters.user?.uid ||
    null;
  const activeOrgUID =
    rootGetters.selectedOrgUID || rootGetters.currentOrgUID || null;
  return (
    !!activeUserUID &&
    !!activeOrgUID &&
    economyRestrictions.ownerUID === activeUserUID &&
    economyRestrictions.orgUID === activeOrgUID
  );
};

export default {
  namespaced: true,
  state: {
    isDarkMode: true, // Default to dark mode
    areMessagesMinimised: false,
    areMessagesOpen: false,
    showInteractiveMessage: false,
    otherUserData: null,
    showWishlist: false,
    productInfo: null,
    interactiveMessageType: null,
    transferAmount: 0,
    unreadMessagesCount: 0,
    showOwnOrganisationOnly: false,
    otherOrgAllowsTrades: null, // null = not checked, true/false = result
    pendingShareItem: null, // Item pending to be shared via Messages
    pendingSiblingProposal: null,
    // Overlay URL state — coordinates global (App.vue) vs local (MasonryMarketplace) overlay
    globalOverlayItem: null, // Item data for the global overlay (App.vue)
    globalOverlayLoading: false, // True while fetching item for global overlay
    localOverlayActive: false, // True when MasonryMarketplace owns the overlay
    // Floating player state
    floatingPlayerItem: null, // Item currently playing in the floating player
    floatingPlayerMinimised: false, // True when player is collapsed to mini bar
    floatingPlayerStartTime: 0, // Seconds to resume playback from
    floatingPlayerPaused: false, // True when float player is paused by conflict dialog
    floatingPlayerPlaylist: [], // List of recently played items
    floatingPlayerQueue: [],    // Items queued to play next
    // Closed economy state
    economyRestrictions: createEconomyRestrictionsState(),
  },
  getters: {
    isDarkMode: (state) => state.isDarkMode,
    areMessagesMinimised: (state) => state.areMessagesMinimised,
    areMessagesOpen: (state) => state.areMessagesOpen,
    showInteractiveMessage: (state) => state.showInteractiveMessage,
    otherUserData: (state) => state.otherUserData,
    showWishlist: (state) => state.showWishlist,
    productInfo: (state) => state.productInfo,
    interactiveMessageType: (state) => state.interactiveMessageType,
    transferAmount: (state) => state.transferAmount,
    unreadMessagesCount: (state) => state.unreadMessagesCount,
    hasUnreadMessages: (state) => state.unreadMessagesCount > 0,
    showOwnOrganisationOnly: (state) => state.showOwnOrganisationOnly,
    otherOrgAllowsTrades: (state) => state.otherOrgAllowsTrades,
    pendingShareItem: (state) => state.pendingShareItem,
    pendingSiblingProposal: (state) => state.pendingSiblingProposal,
    // Overlay URL getters
    globalOverlayItem: (state) => state.globalOverlayItem,
    globalOverlayLoading: (state) => state.globalOverlayLoading,
    localOverlayActive: (state) => state.localOverlayActive,
    // Floating player getters
    floatingPlayerItem: (state) => state.floatingPlayerItem,
    floatingPlayerMinimised: (state) => state.floatingPlayerMinimised,
    floatingPlayerActive: (state) => !!state.floatingPlayerItem,
    floatingPlayerStartTime: (state) => state.floatingPlayerStartTime,
    floatingPlayerPlaylist: (state) => state.floatingPlayerPlaylist,
    floatingPlayerQueue: (state) => state.floatingPlayerQueue,
    floatingPlayerPaused: (state) => state.floatingPlayerPaused,
    // Economy restriction getters
    economyRestrictions: (state) => state.economyRestrictions,
    // Most important getter - determines if trades are allowed based on both org and wallet settings
    // If organization doesn't allow trades, wallet setting is irrelevant
    // If organization allows trades, check wallet setting
    isTradeAllowed: (state, _getters, _rootState, rootGetters) =>
      ownsActiveEconomyContext(state.economyRestrictions, rootGetters) &&
      state.economyRestrictions.loaded &&
      state.economyRestrictions.orgAllowTrades &&
      state.economyRestrictions.walletAllowTrades,
    isEconomyRestrictionsLoaded: (state, _getters, _rootState, rootGetters) =>
      ownsActiveEconomyContext(state.economyRestrictions, rootGetters) &&
      state.economyRestrictions.loaded,
    hasEconomyRestrictionsError: (
      state,
      _getters,
      _rootState,
      rootGetters
    ) =>
      ownsActiveEconomyContext(state.economyRestrictions, rootGetters) &&
      !!state.economyRestrictions.error,
  },
  mutations: {
    // Other org trade permission
    SET_OTHER_ORG_ALLOWS_TRADES(state, value) {
      state.otherOrgAllowsTrades = value;
    },
    // Economy restrictions mutations
    SET_ORG_ALLOW_TRADES(state, value) {
      state.economyRestrictions.orgAllowTrades = !!value;
    },
    SET_WALLET_ALLOW_TRADES(state, value) {
      state.economyRestrictions.walletAllowTrades = !!value;
    },
    SET_ECONOMY_RESTRICTIONS_LOADED(state, value) {
      state.economyRestrictions.loaded = value;
    },
    SET_ECONOMY_RESTRICTIONS_ERROR(state, error) {
      state.economyRestrictions.error = error;
    },
    BEGIN_ECONOMY_RESTRICTIONS_FETCH(state, { userUID, orgUID }) {
      state.economyRestrictions = createEconomyRestrictionsState({
        ownerUID: userUID || null,
        orgUID: orgUID || null,
        requestGeneration:
          state.economyRestrictions.requestGeneration + 1,
      });
    },
    COMPLETE_ECONOMY_RESTRICTIONS_FETCH(
      state,
      {
        userUID,
        orgUID,
        generation,
        orgAllowTrades,
        walletAllowTrades,
        error = null,
      }
    ) {
      if (
        state.economyRestrictions.ownerUID !== userUID ||
        state.economyRestrictions.orgUID !== orgUID ||
        state.economyRestrictions.requestGeneration !== generation
      ) {
        return;
      }

      state.economyRestrictions = createEconomyRestrictionsState({
        ownerUID: userUID,
        orgUID,
        requestGeneration: generation,
        orgAllowTrades: !!orgAllowTrades,
        walletAllowTrades: !!walletAllowTrades,
        loaded: true,
        error,
      });
    },
    RESET_ECONOMY_RESTRICTIONS(state) {
      state.economyRestrictions = createEconomyRestrictionsState({
        requestGeneration:
          state.economyRestrictions.requestGeneration + 1,
      });
    },

    TOGGLE_DARK_MODE(state) {
      state.isDarkMode = !state.isDarkMode;
      appearanceRuntime.setMode(state.isDarkMode ? "dark" : "light");
    },
    INIT_DARK_MODE(state, prefersDark) {
      state.isDarkMode =
        appearanceRuntime.initialize({ prefersDark }) === "dark";
    },
    TOGGLE_MESSAGES_MINIMISED(state) {
      state.areMessagesMinimised = !state.areMessagesMinimised;
    },
    SET_MESSAGES_MINIMISED(state, value) {
      state.areMessagesMinimised = value;
    },
    SET_MESSAGES_OPEN(state, value) {
      state.areMessagesOpen = value;
    },
    SET_OTHER_USER_DATA(state, userData) {
      state.otherUserData = userData;
    },
    SET_PRODUCT_INFO(state, productInfo) {
      state.productInfo = productInfo;
    },
    CLEAR_PRODUCT_INFO(state) {
      state.productInfo = null;
      state.showInteractiveMessage = false;
    },
    SET_MESSAGES_MAXIMISED(state) {
      state.areMessagesMinimised = false;
    },
    SET_SHOW_INTERACTIVE_MESSAGE(state, value) {
      state.showInteractiveMessage = value;
    },
    TOGGLE_WISHLIST(state) {
      state.showWishlist = !state.showWishlist;
    },
    SET_WISHLIST_VISIBILITY(state, visibility) {
      state.showWishlist = visibility;
    },
    SET_INTERACTIVE_MESSAGE_TYPE(state, type) {
      state.interactiveMessageType = type;
    },
    SET_TRANSFER_AMOUNT(state, amount) {
      state.transferAmount = amount;
    },
    SET_UNREAD_MESSAGES_COUNT(state, count) {
      state.unreadMessagesCount = count;
    },
    DECREMENT_UNREAD_MESSAGES_COUNT(state) {
      if (state.unreadMessagesCount > 0) {
        state.unreadMessagesCount--;
      }
    },
    TOGGLE_SHOW_OWN_ORGANISATION_ONLY(state) {
      state.showOwnOrganisationOnly = !state.showOwnOrganisationOnly;
    },
    SET_SHOW_OWN_ORGANISATION_ONLY(state, value) {
      state.showOwnOrganisationOnly = value;
    },
    SET_PENDING_SHARE_ITEM(state, item) {
      state.pendingShareItem = item;
    },
    CLEAR_PENDING_SHARE_ITEM(state) {
      state.pendingShareItem = null;
    },
    SET_PENDING_SIBLING_PROPOSAL(state, proposal) {
      state.pendingSiblingProposal = proposal;
    },
    CLEAR_PENDING_SIBLING_PROPOSAL(state) {
      state.pendingSiblingProposal = null;
    },
    // Overlay URL mutations
    SET_GLOBAL_OVERLAY_ITEM(state, item) {
      state.globalOverlayItem = item;
    },
    CLEAR_GLOBAL_OVERLAY_ITEM(state) {
      state.globalOverlayItem = null;
    },
    RESET_AUTH_SCOPED_PRESENTATION(state) {
      state.areMessagesOpen = false;
      state.showInteractiveMessage = false;
      state.otherUserData = null;
      state.productInfo = null;
      state.pendingShareItem = null;
      state.pendingSiblingProposal = null;
      state.globalOverlayItem = null;
      state.globalOverlayLoading = false;
      state.localOverlayActive = false;
      state.floatingPlayerItem = null;
      state.floatingPlayerMinimised = false;
      state.floatingPlayerStartTime = 0;
      state.floatingPlayerPaused = false;
      state.floatingPlayerPlaylist = [];
      state.floatingPlayerQueue = [];
    },
    SET_GLOBAL_OVERLAY_LOADING(state, value) {
      state.globalOverlayLoading = !!value;
    },
    SET_LOCAL_OVERLAY_ACTIVE(state, value) {
      state.localOverlayActive = !!value;
    },
    // Floating player mutations
    SET_FLOATING_PLAYER_ITEM(state, item) {
      state.floatingPlayerItem = item;
      state.floatingPlayerMinimised = false;
    },
    SET_FLOATING_PLAYER_START_TIME(state, seconds) {
      state.floatingPlayerStartTime = seconds || 0;
    },
    ADD_TO_FLOATING_PLAYER_PLAYLIST(state, item) {
      if (!item) return;
      state.floatingPlayerPlaylist = state.floatingPlayerPlaylist.filter(i => i.id !== item.id);
      state.floatingPlayerPlaylist.unshift(item);
    },
    REMOVE_FROM_FLOATING_PLAYER_PLAYLIST(state, itemId) {
      state.floatingPlayerPlaylist = state.floatingPlayerPlaylist.filter(i => i.id !== itemId);
    },
    ADD_TO_FLOATING_PLAYER_QUEUE(state, item) {
      if (!item || !item.id) return;
      const exists = state.floatingPlayerQueue.some(i => i.id === item.id);
      if (!exists) state.floatingPlayerQueue.unshift(item);
    },
    REMOVE_FROM_FLOATING_PLAYER_QUEUE(state, itemId) {
      state.floatingPlayerQueue = state.floatingPlayerQueue.filter(i => i.id !== itemId);
    },
    SET_FLOATING_PLAYER_PAUSED(state, val) {
      state.floatingPlayerPaused = !!val;
    },
    SET_FLOATING_PLAYER_ITEM_TITLE(state, title) {
      if (state.floatingPlayerItem) {
        state.floatingPlayerItem.fetchedTitle = title;
      }
    },
    CLEAR_FLOATING_PLAYER(state) {
      state.floatingPlayerItem = null;
      state.floatingPlayerMinimised = false;
      state.floatingPlayerStartTime = 0;
      state.floatingPlayerPaused = false;
      state.floatingPlayerQueue = [];
    },
    SET_FLOATING_PLAYER_MINIMISED(state, value) {
      state.floatingPlayerMinimised = !!value;
    },
  },
  actions: {
    // Other org trade permission
    setOtherOrgAllowsTrades({ commit }, value) {
      commit("SET_OTHER_ORG_ALLOWS_TRADES", value);
    },

    // When a user is selected in messages
    async checkAndSetOtherOrgTrades(
      { dispatch },
      _otherUser
    ) {
      // Cross-organisation eligibility is a quote/settlement decision. A
      // conversation presentation never exposes enough authority to infer it.
      dispatch("setOtherOrgAllowsTrades", false);
      return false;
    },

    // Economy restrictions actions
    async fetchEconomyRestrictions(
      { commit, state },
      { orgUID, userUID } = {}
    ) {
      commit("BEGIN_ECONOMY_RESTRICTIONS_FETCH", { userUID, orgUID });
      const generation = state.economyRestrictions.requestGeneration;
      const complete = ({
        orgAllowTrades = false,
        walletAllowTrades = false,
        error = null,
      } = {}) => {
        commit("COMPLETE_ECONOMY_RESTRICTIONS_FETCH", {
          userUID: userUID || null,
          orgUID: orgUID || null,
          generation,
          orgAllowTrades,
          walletAllowTrades,
          error,
        });
      };

      if (!orgUID || !userUID) {
        console.error("fetchEconomyRestrictions: Missing required parameters", {
          orgUID,
          userUID,
        });
        complete({ error: "Missing required parameters" });
        return false;
      }

      try {
        const { organisationContextService } = await import(
          "@/services/organisationContextService"
        );
        const context =
          await organisationContextService.getOwnOrganisationContext(orgUID);
        if (
          context?.orgUID !== orgUID ||
          context.membershipStatus !== "active"
        ) {
          complete({ error: "Organisation not found" });
          return false;
        }
        const orgAllowTrades = context.economyOpen === true;
        if (!orgAllowTrades) {
          complete({ orgAllowTrades: false, walletAllowTrades: false });
          return false;
        }
        // Wallet eligibility, balance and payer selection are revalidated by
        // the server quote. This presentation flag only mirrors the active
        // organisation's private economy policy.
        const walletAllowTrades = true;
        complete({ orgAllowTrades, walletAllowTrades });
        return orgAllowTrades && walletAllowTrades;
      } catch (error) {
        console.error("Error fetching economy restrictions:", error);
        complete({
          error: error.message || "Failed to fetch economy restrictions",
        });
        return false;
      }
    },

    // Check if a specific org allows trades
    async checkOrgAllowsTrades(_context, orgUID) {
      if (!orgUID) return false;
      try {
        const { organisationContextService } = await import(
          "@/services/organisationContextService"
        );
        const context =
          await organisationContextService.getOwnOrganisationContext(orgUID);
        return (
          context?.orgUID === orgUID &&
          context.membershipStatus === "active" &&
          context.economyOpen === true
        );
      } catch (_error) {
        return false;
      }
    },

    // Reset economy restrictions (e.g., on sign out)
    resetEconomyRestrictions({ commit }) {
      commit("RESET_ECONOMY_RESTRICTIONS");
    },

    toggleDarkMode({ commit }) {
      commit("TOGGLE_DARK_MODE");
    },
    initDarkMode({ commit }) {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;
      commit("INIT_DARK_MODE", prefersDark);
    },
    toggleMessagesMinimised({ commit }) {
      commit("TOGGLE_MESSAGES_MINIMISED");
    },
    setMessagesOpen({ commit }, value) {
      commit("SET_MESSAGES_OPEN", value);
    },
    setMessagesMinimised({ commit }, value) {
      commit("SET_MESSAGES_MINIMISED", value);
    },
    setOtherUserData({ commit }, userData) {
      if (userData === null) {
        commit("SET_OTHER_USER_DATA", null);
        return true;
      }
      const presentation = normalizePrivateMessagePresentation(userData);
      commit("SET_OTHER_USER_DATA", presentation);
      return presentation !== null;
    },
    setPendingSiblingProposal({ commit }, proposal) {
      try {
        commit(
          "SET_PENDING_SIBLING_PROPOSAL",
          createSiblingProposalDraft(proposal)
        );
        return true;
      } catch (_error) {
        commit("CLEAR_PENDING_SIBLING_PROPOSAL");
        return false;
      }
    },
    clearPendingSiblingProposal({ commit }) {
      commit("CLEAR_PENDING_SIBLING_PROPOSAL");
    },
    setMessagesMaximised({ commit }) {
      commit("SET_MESSAGES_MAXIMISED");
    },
    setShowInteractiveMessage({ commit }, value) {
      commit("SET_SHOW_INTERACTIVE_MESSAGE", false);
      return value === false;
    },
    setProductInfo({ commit }, productInfo) {
      commit("CLEAR_PRODUCT_INFO");
      return productInfo === null;
    },
    clearProductInfo({ commit }) {
      commit("CLEAR_PRODUCT_INFO");
    },
    setTransferAmount({ commit }, amount) {
      commit("SET_TRANSFER_AMOUNT", amount);
    },
    async fetchUnreadMessagesCount({ commit }, userUid) {
      void userUid;
      // Receipt state is not part of the bounded thread presentation yet.
      commit("SET_UNREAD_MESSAGES_COUNT", 0);
    },
    async markMessageAsRead() {
      // Read receipts will use the participant-bound Messages command.
      return false;
    },
    async fetchOtherUserData(
      { commit },
      _payload
    ) {
      // Starting a thread from commerce needs a server-owned contact policy.
      commit("SET_OTHER_USER_DATA", null);
      commit("CLEAR_PRODUCT_INFO");
      return false;
    },
    toggleWishlist({ commit }) {
      commit("TOGGLE_WISHLIST");
    },
    setWishlistVisibility({ commit }, visibility) {
      commit("SET_WISHLIST_VISIBILITY", visibility);
    },
    setInteractiveMessageType({ commit }, type) {
      commit("SET_INTERACTIVE_MESSAGE_TYPE", type);
    },
    toggleShowOwnOrganisationOnly({ commit }) {
      commit("TOGGLE_SHOW_OWN_ORGANISATION_ONLY");
    },
    setShowOwnOrganisationOnly({ commit }, value) {
      commit("SET_SHOW_OWN_ORGANISATION_ONLY", value);
    },
    setPendingShareItem({ commit }, item) {
      commit("SET_PENDING_SHARE_ITEM", item);
    },
    clearPendingShareItem({ commit }) {
      commit("CLEAR_PENDING_SHARE_ITEM");
    },
    // Overlay URL actions
    setGlobalOverlayItem({ commit }, item) {
      commit("SET_GLOBAL_OVERLAY_ITEM", item);
    },
    clearGlobalOverlayItem({ commit }) {
      commit("CLEAR_GLOBAL_OVERLAY_ITEM");
    },
    resetForAuthChange({ commit }) {
      commit("RESET_AUTH_SCOPED_PRESENTATION");
    },
    resetForContextChange({ commit }) {
      commit("RESET_AUTH_SCOPED_PRESENTATION");
    },
    setGlobalOverlayLoading({ commit }, value) {
      commit("SET_GLOBAL_OVERLAY_LOADING", value);
    },
    setLocalOverlayActive({ commit }, value) {
      commit("SET_LOCAL_OVERLAY_ACTIVE", value);
    },
    // Floating player actions
    playInFloatingPlayer({ commit, state }, { item, startTime = 0 } = {}) {
      if (state.floatingPlayerItem && state.floatingPlayerItem.id !== item.id) {
        commit("ADD_TO_FLOATING_PLAYER_PLAYLIST", state.floatingPlayerItem);
      }
      commit("REMOVE_FROM_FLOATING_PLAYER_PLAYLIST", item.id);
      commit("REMOVE_FROM_FLOATING_PLAYER_QUEUE", item.id);
      commit("SET_FLOATING_PLAYER_ITEM", item);
      commit("SET_FLOATING_PLAYER_START_TIME", startTime);
      commit("SET_FLOATING_PLAYER_PAUSED", false);
    },
    closeFloatingPlayer({ commit, state }) {
      if (state.floatingPlayerItem) {
        commit("ADD_TO_FLOATING_PLAYER_PLAYLIST", state.floatingPlayerItem);
      }
      commit("CLEAR_FLOATING_PLAYER");
    },
    pauseFloatingPlayer({ commit }) {
      commit("SET_FLOATING_PLAYER_PAUSED", true);
    },
    resumeFloatingPlayer({ commit }) {
      commit("SET_FLOATING_PLAYER_PAUSED", false);
    },
    addItemToFloatingPlayerPlaylist({ commit }, item) {
      commit("ADD_TO_FLOATING_PLAYER_PLAYLIST", item);
    },
    addItemToFloatingPlayerQueue({ commit }, item) {
      commit("ADD_TO_FLOATING_PLAYER_QUEUE", item);
    },
    minimiseFloatingPlayer({ commit }) {
      commit("SET_FLOATING_PLAYER_MINIMISED", true);
    },
    expandFloatingPlayer({ commit }) {
      commit("SET_FLOATING_PLAYER_MINIMISED", false);
    },
  },
};
